import { DateFieldFilterEnum, RollingPeriodValue } from "../common/components/filterPopover/subMenues/DateFieldSubMenu.tsx";
import { FilterOption } from "../common/components/filterPopover/FilterPopover.types.ts";
import { CustomFieldFilter, FilterOperatorUi, ListFilter, ListFilterOperator, SortDirection, TextFilter, TextFilterOperator } from "@common/api/filterTypes.ts";
import { uiOperatorToWire } from "@common/components/filterBar/filterOperatorHydration.ts";
import { hasFilterValue } from "@common/components/filterBar/filterPinning.ts";
import { ContractFilterStatus } from "./Contracts.types.ts";
import { ContractColumn } from "./contractDisplayFields/ContractDisplayFields.types.ts";

export { ListFilterOperator, SortDirection, TextFilterOperator } from "@common/api/filterTypes.ts";

export const defaultContractPageSize = 50;
export interface ContractFilterRequest {
    // Basic filters
    id?: ListFilter<string>; // Guid as string
    templateIds?: ListFilter<string>;
    contractTypeIds?: ListFilter<string>;
    status?: ListFilter<ContractFilterStatus>; // Note: C# has Status, not statuses

    // Access filters
    contractManagerIds?: ListFilter<string>;
    visibleToAllUsers?: boolean;
    additionalUserIds?: ListFilter<string>;
    excludedUserIds?: ListFilter<string>;
    accessBusinessAreaIds?: ListFilter<string>;

    // Organization filters
    businessAreaIds?: ListFilter<string>;
    legalEntityIds?: ListFilter<string>;
    externalContractPartyIds?: ListFilter<string>;
    internalContractPartyIds?: ListFilter<string>;
    hasBusinessArea?: boolean;
    hasExternalContractParty?: boolean;

    // Date range filters
    startDateRange?: DateRangeFilter;
    endDateRange?: DateRangeFilter;
    renewalDateRange?: DateRangeFilter;
    signatureDateRange?: DateRangeFilter;
    createdDateRange?: DateRangeFilter;
    modifiedDateRange?: DateRangeFilter;

    // Financial filters
    currencyCodes?: ListFilter<string>;
    totalAmount?: AmountRangeFilter;
    finalizedAmount?: AmountRangeFilter;
    hasFinalizedAmount?: boolean;

    // Document & metadata filters
    documentIds?: ListFilter<string>;
    paymentTermIds?: ListFilter<string>;

    // Text search filters
    description?: TextFilter;
    note?: TextFilter;
    specialTerms?: TextFilter;
    referenceNumber?: TextFilter;
    searchText?: string;

    // Notification filters
    expiryNotificationDays?: AmountRangeFilter;
    withinExpiryNotificationPeriod?: boolean;

    // Show only mine
    showOnlyMine?: boolean;

    // Tag filters
    tagIds?: ListFilter<string>;

    // Custom field filters
    customFieldFilters?: CustomFieldFilter[];

    // Ids of filters the user left on screen without a value. Presentation state only - it rides along so it
    // survives a saved view round trip, and is stripped before the filter becomes a query key or a request body.
    pinnedFilterIds?: Array<string>;

    // Pagination & Sorting
    pageNumber?: number; // Default: 1
    pageSize?: number; // Default: 50
    sortByColumn?: ContractColumn; // Default: ContractSortField.UserFriendlyId
    sortByCustomFieldId?: string;
    sortDirection?: SortDirection; // Default: SortDirection.ASC
}

export interface DateRangeFilter {
    from?: string; // ISO date string
    to?: string; // ISO date string
    dateFieldEnum?: DateFieldFilterEnum;
    rollingPeriod?: RollingPeriodValue;
}

// Same structure for the request
export interface AmountRangeFilter {
    min?: number;
    max?: number;
}

export function transformFilterOptionsToBackend(
    filterOptions: FilterOption[],
    sortByColumn?: ContractColumn,
    sortByCustomFieldId?: string,
    sortDirection?: SortDirection
): Partial<ContractFilterRequest> {
    const result: Partial<ContractFilterRequest> = {};

    filterOptions.forEach((option) => {
        if (!hasFilterValue(option.value)) {
            return;
        }

        if (option.id.startsWith("custom-field-")) {
            // Handle individual custom field
            if (!result.customFieldFilters) {
                result.customFieldFilters = [];
            }

            // Extract the custom field data from the option value
            result.customFieldFilters.push({
                inputFieldId: option.id.replace("custom-field-", ""),
                value: option.value,
                operator: uiOperatorToWire(option.filterOperator, option.type),
            });
            return;
        }

        const backendProperty = FILTER_ID_TO_BACKEND_PROPERTY[option.id];
        if (!backendProperty) {
            return;
        }

        // Type-safe assignment based on property name
        switch (backendProperty) {
            // Direct boolean
            case "visibleToAllUsers":
            case "showOnlyMine":
            case "withinExpiryNotificationPeriod":
            case "hasBusinessArea":
            case "hasExternalContractParty":
            case "hasFinalizedAmount":
                result[backendProperty] = option.value as boolean;
                break;

            // Direct array of custom field filters
            case "customFieldFilters":
                result[backendProperty] = option.value as CustomFieldFilter[];
                break;

            // DateRangeFilter properties
            case "startDateRange":
            case "endDateRange":
            case "renewalDateRange":
            case "signatureDateRange":
            case "createdDateRange":
            case "modifiedDateRange": {
                // If value is already a DateRangeFilter object
                if (
                    typeof option.value === "object" &&
                    ("from" in option.value || "to" in option.value || "dateFieldEnum" in option.value || "rollingPeriod" in option.value)
                ) {
                    result[backendProperty] = option.value as DateRangeFilter;
                }
                break;
            }

            // AmountRangeFilter properties
            case "totalAmount":
            case "finalizedAmount":
            case "expiryNotificationDays": {
                // If value is already an AmountRangeFilter object
                if (typeof option.value === "object" && ("from" in option.value || "to" in option.value)) {
                    result[backendProperty] = option.value as AmountRangeFilter;
                }
                break;
            }

            // TextFilter properties
            case "description":
            case "note":
            case "specialTerms":
            case "referenceNumber":
                // If value is a string, create TextFilter
                if (typeof option.value === "string") {
                    result[backendProperty] = {
                        value: option.value,
                        filterOperator: option.filterOperator === FilterOperatorUi.Negative ? TextFilterOperator.NotContains : TextFilterOperator.Contains,
                    };
                } else if (typeof option.value === "object" && "value" in option.value && "filterOperator" in option.value) {
                    result[backendProperty] = option.value as TextFilter;
                }
                break;

            // Pagination & Sorting
            case "pageNumber":
            case "pageSize":
                result[backendProperty] = option.value as number;
                break;

            case "sortByColumn":
                result[backendProperty] = option.value as ContractColumn;
                break;

            case "sortDirection":
                result[backendProperty] = option.value as SortDirection;
                break;

            // ListFilter properties - all need to be wrapped in ListFilter structure
            case "id":
            case "templateIds":
            case "contractTypeIds":
            case "contractManagerIds":
            case "additionalUserIds":
            case "excludedUserIds":
            case "accessBusinessAreaIds":
            case "businessAreaIds":
            case "legalEntityIds":
            case "externalContractPartyIds":
            case "internalContractPartyIds":
            case "documentIds":
            case "paymentTermIds":
            case "tagIds":
            case "currencyCodes":
                // If value is an array, create ListFilter
                if (Array.isArray(option.value)) {
                    result[backendProperty] = {
                        value: option.value,
                        filterOperator: option.filterOperator === FilterOperatorUi.Negative ? ListFilterOperator.NotIn : ListFilterOperator.In,
                    };
                } else if (typeof option.value === "object" && "value" in option.value && "filterOperator" in option.value) {
                    // Already a ListFilter structure
                    result[backendProperty] = option.value as ListFilter<string>;
                }
                break;

            case "status": {
                // Special handling for status enum
                if (Array.isArray(option.value)) {
                    result[backendProperty] = {
                        value: option.value.map(Number),
                        filterOperator: option.filterOperator === FilterOperatorUi.Negative ? ListFilterOperator.NotIn : ListFilterOperator.In,
                    } as ListFilter<ContractFilterStatus>;
                } else if (typeof option.value === "object" && "value" in option.value && "filterOperator" in option.value) {
                    result[backendProperty] = option.value as ListFilter<ContractFilterStatus>;
                }
                break;
            }
        }
    });

    // Add default pagination if not provided

    result.pageNumber = 1;

    if (result.pageSize === undefined) {
        result.pageSize = defaultContractPageSize;
    }

    if (result.sortDirection === undefined) {
        result.sortDirection = SortDirection.ASC;
    }

    result.sortByColumn = sortByColumn;
    result.sortByCustomFieldId = sortByCustomFieldId;
    result.sortDirection = sortDirection;

    return result;
}

export const FILTER_ID_TO_BACKEND_PROPERTY: Record<string, string> = {
    "contract-ids": "id",
    "template-ids": "templateIds",
    "contract-type-ids": "contractTypeIds",
    statuses: "status",

    // People & Access filters
    "contract-manager-ids": "contractManagerIds",
    "visible-to-all": "visibleToAllUsers",
    "additional-user-ids": "additionalUserIds",
    "excluded-user-ids": "excludedUserIds",

    // Organization filters
    "business-area-ids": "businessAreaIds",
    "access-business-area-ids": "accessBusinessAreaIds",
    "legal-entity-ids": "legalEntityIds",
    "external-contractParty-ids": "externalContractPartyIds",
    "internal-contractParty-ids": "internalContractPartyIds",
    "has-business-area": "hasBusinessArea",
    "has-external-contract-party": "hasExternalContractParty",

    // Date range filters
    "start-date-range": "startDateRange",
    "end-date-range": "endDateRange",
    "renewal-date-range": "renewalDateRange",
    "signature-date-range": "signatureDateRange",
    "created-date-range": "createdDateRange",
    "modified-date-range": "modifiedDateRange",

    // Financial filters
    "currency-codes": "currencyCodes",
    "total-amount-range": "totalAmount",
    "finalized-amount-range": "finalizedAmount",
    "has-finalized-amount": "hasFinalizedAmount",
    "payment-term-ids": "paymentTermIds",

    // Text search filters
    description: "description",
    note: "note",
    "special-terms": "specialTerms",
    "reference-number": "referenceNumber",

    // Notification filters
    "expiry-notification-days": "expiryNotificationDays",
    "within-expiry-notification-period": "withinExpiryNotificationPeriod",

    // Show only mine
    "show-only-mine": "showOnlyMine",

    // Tag filters
    "tag-ids": "tagIds",
    "tag-operator": "tagOperator",

    // Custom field filters (handled separately)
    "custom-fields": "customFieldFilters",
};
